/* eslint-disable no-await-in-loop */
/* eslint-disable react-refresh/only-export-components */
/* eslint-disable react/only-export-components */
import Script from 'next/script'
import { useEffect, useState } from 'react'
import Head from 'next/head'
import * as Sentry from '@sentry/nextjs'

import sha256 from './sha256'
import cleanPhone from './cleanPhone'
import {
  DO_NOT_TRACK_COUNTRIES,
  isTrackingOptedOut
} from './privacyPreferences'

const ENABLED = process.env.NODE_ENV === 'production'
const LOG_TO_CONSOLE = process.env.NODE_ENV === 'development'

const IS_SERVER = typeof window === 'undefined'

// Updates Google Consent Mode to reflect the current opt-out state. Called after
// hydration and whenever the user changes their privacy choice, so the Google tags
// (loaded with a denied-by-default consent) get granted/denied accordingly.
export const applyConsent = () => {
  if (IS_SERVER) return

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { gtag } = window as any
  if (!gtag) return

  const granted = isTrackingOptedOut() ? 'denied' : 'granted'
  gtag('consent', 'update', {
    ad_storage: granted,
    ad_user_data: granted,
    ad_personalization: granted,
    analytics_storage: granted
  })
}

// Inline, pre-hydration script that sets the Consent Mode default before the GA
// config call. It must read the opt-out state synchronously, so the logic from
// privacyPreferences is duplicated here in plain JS.
const CONSENT_DEFAULT_SCRIPT = `
  (function() {
    var doNotTrack = ${JSON.stringify(DO_NOT_TRACK_COUNTRIES)};
    var optedOut = false;
    try {
      var geo = document.cookie.match(/(?:^|;\\s*)geo-country=([^;]*)/);
      if (geo && doNotTrack.indexOf(geo[1]) !== -1) {
        optedOut = true;
      } else {
        var stored = document.cookie.match(/(?:^|;\\s*)BFT_DO_NOT_SELL=([^;]*)/);
        var choice = stored ? decodeURIComponent(stored[1]) : localStorage.getItem('BFT_DO_NOT_SELL');
        if (choice === 'opted_out') optedOut = true;
        else if (choice !== 'opted_in') optedOut = navigator.globalPrivacyControl === true;
      }
    } catch (e) {}
    var v = optedOut ? 'denied' : 'granted';
    gtag('consent', 'default', {
      ad_storage: v,
      ad_user_data: v,
      ad_personalization: v,
      analytics_storage: v
    });
  })();
`

// Meta, LinkedIn and Bing have no Consent Mode equivalent, so we simply don't inject
// their scripts when the user has opted out. Rendered client-side after hydration to
// read the opt-out state (which lives in a cookie / GPC).
function AdTrackers() {
  const [optedOut, setOptedOut] = useState(true)

  useEffect(() => {
    setOptedOut(isTrackingOptedOut() !== null)
  }, [])

  if (optedOut) return null

  return (
    <>
      {/* <Script id="meta-pixel" strategy="afterInteractive">
        {`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '628919715981294');
          fbq('track', 'PageView');
        `}
      </Script> */}
      <Script id="linked-in-tracking" strategy="afterInteractive">
        {`
          _linkedin_partner_id = "5524354"; window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || []; window._linkedin_data_partner_ids.push(_linkedin_partner_id);
          window._linkedin_data_partner_ids.push("9640820");
           (function(l) { if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])}; window.lintrk.q=[]} var s = document.getElementsByTagName("script")[0]; var b = document.createElement("script"); b.type = "text/javascript";b.async = true; b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js"; s.parentNode.insertBefore(b, s);})(window.lintrk);
        `}
      </Script>
      <Script id="bing-tracking" strategy="afterInteractive">
        {`
            (function(w,d,t,r,u)
            {
              var f,n,i;
              w[u]=w[u]||[],f=function()
              {
                var o={ti:"97106542", enableAutoSpaTracking: true};
                o.q=w[u],w[u]=new UET(o),w[u].push("pageLoad")
              },
              n=d.createElement(t),n.src=r,n.async=1,n.onload=n.onreadystatechange=function()
              {
                var s=this.readyState;
                s&&s!=="loaded"&&s!=="complete"||(f(),n.onload=n.onreadystatechange=null)
              },
              i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)
            })
            (window,document,"script","//bat.bing.com/bat.js","uetq");
        `}
      </Script>
      <Script id="apollo-tracking" strategy="afterInteractive">
        {`
          function initApollo(){var n=Math.random().toString(36).substring(7),o=document.createElement("script");
          o.src="https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache="+n,o.async=!0,o.defer=!0,
          o.onload=function(){window.trackingFunctions.onLoad({appId:"69b0e83ed554e800196cd5e5"})},
          document.head.appendChild(o)}initApollo();
        `}
      </Script>
      {/* <noscript>
        <img
          height="1"
          src="https://www.facebook.com/tr?id=628919715981294&ev=PageView&noscript=1"
          style={{ display: 'none' }}
          width="1"
        />
      </noscript> */}
      <noscript>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          alt=""
          height="1"
          src="https://px.ads.linkedin.com/collect/?pid=5524354&fmt=gif"
          style={{ display: 'none' }}
          width="1"
        />
      </noscript>
      <noscript>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          alt=""
          height="1"
          src="https://px.ads.linkedin.com/collect/?pid=9640820&fmt=gif"
          style={{ display: 'none' }}
          width="1"
        />
      </noscript>
    </>
  )
}

export function AnalyticsScript() {
  if (!ENABLED) return <script />

  return (
    <>
      <Head>
        {/* https://nextjs.org/docs/messages/no-before-interactive-script-outside-document */}
        <script
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: `
           window.dataLayerPending = []

           function gtag(){
            window.dataLayerPending.push(arguments)
            if (window.dataLayer) {
              while(window.dataLayerPending.length > 0) {
                var action = window.dataLayerPending.shift()
                window.dataLayer.push(action)
              }
            }
           }
          `
          }}
        />
      </Head>
      <Script
        src="https://www.googletagmanager.com/gtag/js?id=G-7FQCWZ8F3X"
        strategy="afterInteractive"
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          gtag('js', new Date());

          ${CONSENT_DEFAULT_SCRIPT}

          gtag('config', 'G-7FQCWZ8F3X');
          gtag('config', 'AW-768772864');
        `}
      </Script>
      <AdTrackers />
    </>
  )
}

export const logPageView = () => {
  if (!ENABLED) return

  if (IS_SERVER) return

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { gtag, fbq } = window as any

  if (gtag) {
    gtag('config', 'G-7FQCWZ8F3X', {
      page_path: window.location.pathname
    })
  }
  if (fbq) {
    fbq('track', 'PageView')
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const logEvent = (type: string, payload: any) => {
  if (!ENABLED) {
    // eslint-disable-next-line
    if (LOG_TO_CONSOLE) console.log({ gtag: 'event', type, payload })
    return
  }

  if (IS_SERVER) return

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { gtag } = window as any

  if (!gtag) return

  try {
    gtag('event', type, payload)
  } catch (e) {
    Sentry.captureException(e)
  }
}

type GoogleTagUserData = {
  email: string
  phone?: string | null
  firstName?: string | null
  lastName?: string | null
}

const normalizeText = (text: string) => text.toLowerCase().trim()

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setAdsUserData = async (
  ...userDatas: (GoogleTagUserData | null)[]
) => {
  if (isTrackingOptedOut()) return

  const payload: {
    sha256_email_address: string[]
    sha256_phone_number?: string[]
    address?: {
      sha256_first_name: string
      sha256_last_name: string
    }[]
  } = {
    sha256_email_address: []
  }

  try {
    for (const userData of userDatas) {
      if (!userData) {
        // eslint-disable-next-line no-continue
        continue
      }
      payload.sha256_email_address.push(
        await sha256(normalizeText(userData.email))
      )
      if (userData.phone) {
        payload.sha256_phone_number = payload.sha256_phone_number || []
        payload.sha256_phone_number.push(
          await sha256(`+1${cleanPhone(userData.phone)}`)
        )
      }
      if (userData.firstName && userData.lastName) {
        payload.address = payload.address || []
        payload.address.push({
          sha256_first_name: await sha256(normalizeText(userData.firstName)),
          sha256_last_name: await sha256(normalizeText(userData.lastName))
        })
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e)
    Sentry.captureException(e)
    return
  }

  if (!ENABLED) {
    // eslint-disable-next-line
    if (LOG_TO_CONSOLE) console.log({ gtag: 'set', type: 'user_data', payload })
    return
  }

  if (IS_SERVER) return

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { gtag } = window as any

  if (!gtag) return

  try {
    gtag('set', 'user_data', payload)
  } catch (e) {
    Sentry.captureException(e)
  }
}
